import * as React from 'react';
import moment from 'moment-timezone';

import { Action, Dispatch, bindActionCreators } from 'redux';
import { CheckboxVisibility, ConstrainMode, DetailsList, IColumn, Spinner, SpinnerSize, TextField, Selection } from '@fluentui/react';
import { onPageDidMount, onPageWillUnmount } from '../../redux/core';
import { setOnSiteVisitFilterText, setVisitCode } from '../../redux/ui/actions';

import { AsyncData } from '../../redux/core';
import { MultiSelector } from '../core/MultiSelector';
import { OnSiteDayRangeSelector } from './OnSiteDayRangeSelector';
import { OnSiteVisit } from '../../redux/on-site-manager/on-site-visits/types';
import { PageSidePanel } from '../core/PageSidePanel';
import { RootState } from '../../redux/types';
import { VisitSidePanel } from '../visit/VisitSidePanel';
import { VisitStatusIcon } from '../visit/VisitStatusIcon';
import { VisitTag } from '../visit/VisitTag';
import { connect } from 'react-redux';
import { friendlyAge } from '../../util/Date';
import TrieSearch from 'trie-search';

import { Market } from '../../api/markets.types';

const VISIT_STATUS_OPTIONS = [
  { key: 'NEW', text: 'New' },
  { key: 'CANCELLED', text: 'Cancelled' },
  { key: 'FULLY_PAID', text: 'Fully Paid' },
];

export interface OwnProps {}

export interface StateProps {
  markets: AsyncData<Market[]>;
  date: string;
  visits: AsyncData<OnSiteVisit[]>;
  visitCodeToVisitIndex: { [code: string]: number };
  visitFilterText: string;
  visitCode: string;
}

export interface DispatchProps {
  actions: {
    onPageDidMount: (name: string) => void;
    onPageWillUnmount: (name: string) => void;
    setVisitCode: (code: string) => void;
    setOnSiteVisitFilterText: (text: string) => void;
  };
}

type OnSiteVisitsProps = OwnProps & StateProps & DispatchProps;

export interface OnSiteVisitsState {
  items: OnSiteVisit[];
  columns: IColumn[];
  visitStatus: string[];
}

class Component extends React.Component<OnSiteVisitsProps, OnSiteVisitsState> {
  private trie: any;
  private selection: Selection;

  constructor(props: OnSiteVisitsProps) {
    super(props);
    this.state = {
      items: [],
      columns: this.buildColumns(),
      visitStatus: ['NEW', 'CANCELLED', 'FULLY_PAID'],
    };

    this.selection = new Selection({
      getKey: (data: any) => data.visitCode,
      canSelectItem: (data: any) => !!data.visitCode,
    });
  }

  public componentDidMount() {
    this.props.actions.onPageDidMount('OnSiteVisits');
  }

  public componentWillUnmount() {
    this.props.actions.onPageWillUnmount('OnSiteVisits');
  }

  public componentDidUpdate(prevProps: OnSiteVisitsProps) {
    if (this.props.visits !== prevProps.visits && this.props.visits.data) {
      this.updateItems(this.props, true);
    }

    if (this.props.visitFilterText !== prevProps.visitFilterText && this.props.visits.data) {
      this.updateItems(this.props, false);
    }

    if (this.props.visitCode && prevProps.visitCode !== this.props.visitCode) {
      this.selection.setKeySelected(this.props.visitCode, true, true);
    }
  }

  public render(): JSX.Element {
    return (
      <div>
        <div className="flex">
          <div>
            <OnSiteDayRangeSelector />
          </div>
          <div style={{ marginRight: 10 }}>
            <MultiSelector
              placeholder="Select status"
              disabled={this.props.visits.isFetching}
              items={VISIT_STATUS_OPTIONS}
              selectedKeys={this.state.visitStatus}
              onChange={this.onVisitStatusChange}
            />
          </div>
          {this.props.visits.isFetching && <Spinner size={SpinnerSize.large} />}
          <div className="flex" style={{ marginLeft: 'auto', marginRight: 10 }}>
            <div style={{ marginRight: 10 }}>{this.renderFilterControl()}</div>
            {this.renderResultsCount()}
          </div>
        </div>
        <DetailsList
          constrainMode={ConstrainMode.horizontalConstrained}
          checkboxVisibility={CheckboxVisibility.hidden}
          items={this.state.items}
          setKey="set"
          columns={this.state.columns}
          onActiveItemChanged={this.onActiveItemChanged}
          selection={this.selection}
        />
        {this.renderSidePanel()}
      </div>
    );
  }

  private updateItems(props: OnSiteVisitsProps, rebuildTrie = false) {
    if (props.visits.data) {
      const items =
        this.state.visitStatus.length === VISIT_STATUS_OPTIONS.length ? props.visits.data : props.visits.data.filter((v) => this.state.visitStatus.indexOf(v.status) >= 0);
      if (rebuildTrie) {
        this.trie = new TrieSearch(['visitCode', 'buildPatientFullName', 'buildDoctorFullName', 'buildAssistantFullName', 'campaignName']);
        this.trie.addAll(items);
      }
      if (this.props.visitFilterText) {
        this.setState({ items: this.trie.get(this.props.visitFilterText) });
      } else {
        this.setState({ items });
      }
    }
  }

  private onVisitStatusChange = (visitStatus: string[]) => {
    this.setState({ visitStatus }, () => this.updateItems(this.props, true));
  };

  private renderFilterControl(): JSX.Element | null {
    if (!this.props.visits.data || this.props.visits.data?.length === 0) {
      return null;
    }
    return <TextField iconProps={{ iconName: 'Filter' }} placeholder="Filter visits" value={this.props.visitFilterText} onChange={this.onSearchChange} />;
  }

  private renderResultsCount(): JSX.Element | null {
    if (!this.props.visits.data || this.props.visits.data?.length === 0) {
      return null;
    }

    const visible = this.state.items.length;
    const actual = this.props.visits.data?.length;
    if (visible === actual) {
      return <span style={{ fontSize: 20 }}> Results: {visible}</span>;
    } else {
      return (
        <span style={{ fontSize: 20 }}>
          {' '}
          Results: {visible}/{actual}
        </span>
      );
    }
  }

  private onActiveItemChanged = (item: OnSiteVisit) => {
    this.props.actions.setVisitCode(item.visitCode);
  };

  private onSearchChange = (event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) => {
    this.props.actions.setOnSiteVisitFilterText(newValue!);
  };

  private renderSidePanel(): JSX.Element | null {
    if (!this.props.visitCode || !this.props.visits.data || !this.props.visitCodeToVisitIndex) {
      return null;
    }
    const index = this.props.visitCodeToVisitIndex[this.props.visitCode];
    const visit = this.props.visits.data[index];
    if (!visit) {
      return null;
    }

    return (
      <PageSidePanel>
        <VisitSidePanel visit={visit} />
      </PageSidePanel>
    );
  }

  private buildColumns(): IColumn[] {
    return [
      {
        key: 'insurance',
        name: '',
        fieldName: 'insurance',
        minWidth: 60,
        maxWidth: 60,
        onRender: (item?: any) => {
          return <VisitTag visit={item} />;
        },
      },
      {
        key: 'status',
        name: 'Status',
        fieldName: 'status',
        minWidth: 70,
        maxWidth: 70,
        onRender: (item?: any) => {
          return <VisitStatusIcon status={item.status || item.visitStatus} />;
        },
        isMultiline: true,
      },
      {
        key: 'visitcode',
        name: 'Visit code',
        fieldName: 'visitCode',
        minWidth: 80,
        maxWidth: 80,
      },
      {
        key: 'campaign',
        name: 'On-Site event name',
        fieldName: 'campaignName',
        minWidth: 150,
        maxWidth: 150,
      },
      {
        key: 'service',
        name: 'Service',
        fieldName: 'visitServiceName',
        minWidth: 150,
        maxWidth: 150,
        isMultiline: true,
      },
      {
        key: 'patient',
        name: 'Patient',
        fieldName: 'buildPatientFullName',
        minWidth: 80,
        maxWidth: 140,
        isMultiline: true,
        onRender: (item: OnSiteVisit) => {
          return (
            <div>
              <div>{item.buildPatientFullName}</div>
              <div style={{ fontWeight: 'bold' }}>{friendlyAge(item.patientDob)}</div>
            </div>
          );
        },
      },
      {
        key: 'date',
        name: 'Date',
        fieldName: '',
        minWidth: 120,
        maxWidth: 120,
        onRender: (item: OnSiteVisit) => moment(item.timeslotStartTime).tz(item.timezone).format('MM/DD/YYYY'),
      },
      {
        key: 'time',
        name: 'Req. time',
        fieldName: '',
        minWidth: 120,
        maxWidth: 120,
        onRender: (item: OnSiteVisit) => {
          const mtzStart = moment(item.timeslotStartTime).tz(item.timezone);
          const mtzEnd = moment(item.timeslotEndTime).tz(item.timezone);
          if (mtzStart.format('A') === mtzEnd.format('A')) {
            return (
              <div>
                {mtzStart.format('h:mm')} - {mtzEnd.format('h:mm A z')}{' '}
              </div>
            );
          } else {
            return (
              <div>
                {mtzStart.format('h:mm A')} - {mtzEnd.format('h:mm A z')}{' '}
              </div>
            );
          }
        },
      },
      {
        key: 'reschedules',
        name: 'Reschedules',
        fieldName: 'reschedules',
        minWidth: 60,
        maxWidth: 60,
      },
    ];
  }
}

function mapStateToProps(state: RootState): StateProps {
  return {
    markets: state.markets.markets,
    date: state.ui.date,
    visits: state.onSiteManager.onSiteVisits.onSiteVisits,
    visitCodeToVisitIndex: state.onSiteManager.onSiteVisits.onSiteVisitCodeToVisitIndex,
    visitFilterText: state.ui.onSiteVisitFilterText,
    visitCode: state.ui.visitCode,
  };
}

function mapDispatchToProps(dispatch: Dispatch<Action>): DispatchProps {
  return {
    actions: bindActionCreators({ setVisitCode, onPageDidMount, onPageWillUnmount, setOnSiteVisitFilterText }, dispatch),
  };
}

export const OnSiteVisits = connect(mapStateToProps, mapDispatchToProps)(Component);
